/**
 * Mass ↔ amount of substance ↔ number of particles, for one substance.
 *
 *   n = m / M        (mol = g ÷ g/mol)
 *   N = n × N_A      (particles = mol × 6.02214076 × 10²³ per mol)
 *
 * `N` counts whatever the formula describes: molecules for a covalent compound, formula
 * units for an ionic one, atoms for an element written as a single symbol.
 */

import { AVOGADRO } from "@/lib/tool-kit/chemistry/elements";

export type KnownQuantity = "mass" | "amount" | "particles";

export interface Amounts {
  /** grams */
  grams: number;
  /** moles */
  moles: number;
  particles: number;
}

/**
 * All three quantities from the one that is known.
 *
 * `value` is in base units: grams, moles, or a plain count of particles.
 * `molarMass` is in g/mol and must be greater than zero.
 */
export function amountsFrom(
  known: KnownQuantity,
  value: number,
  molarMass: number
): Amounts {
  if (known === "mass") {
    const moles = value / molarMass;
    return { grams: value, moles, particles: moles * AVOGADRO };
  }
  if (known === "amount") {
    return {
      grams: value * molarMass,
      moles: value,
      particles: value * AVOGADRO,
    };
  }
  const moles = value / AVOGADRO;
  return { grams: moles * molarMass, moles, particles: value };
}
